import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Plus,
  X,
  Wand2,
} from "lucide-react";
import {
  analyzeWebsite,
  generateCampaignStrategy,
  expandProfileText,
} from "../../services/collavueApi";
import {
  createCampaign,
  getBrandVoice,
  saveBrandVoice,
  collavueSyncCost,
  collavueRedditSurcharge,
  CAMPAIGN_GOALS,
} from "../../services/collavueStore";
import { useToast } from "../../contexts/ToastProvider";
import PageHeader from "../../components/collavue/PageHeader";

const TONES = [
  "professional",
  "casual",
  "founder-led",
  "technical",
  "witty",
  "direct",
];

// Ready-made starting points for freelancers. Each preset pre-fills the
// profile with enough detail to clear the 80-character minimums, so a
// freelancer can go from "new campaign" to a generated strategy without
// writing a business profile from scratch. Everything stays editable on the
// Confirm Profile step.
const FREELANCE_PRESETS = [
  {
    id: "ugc",
    label: "UGC creator",
    industry: "UGC content creation",
    offer:
      "UGC videos, product demos, testimonials, and short-form content for brands (TikTok, Reels, Shorts)",
    target_customer:
      "DTC brands, e-commerce stores, and marketing agencies that need authentic creator content",
    short_description:
      "I create UGC-style content for brands — authentic product demos, testimonials, unboxings, and short-form videos designed to convert on TikTok, Instagram Reels, and YouTube Shorts.",
    mission:
      "Help brands win trust and sales with authentic creator-made content that feels native to the feed instead of polished ads people scroll straight past.",
  },
  {
    id: "design",
    label: "Graphic designer",
    industry: "Graphic design",
    offer:
      "Logo design, brand identity, social media graphics, pitch decks, and marketing assets",
    target_customer:
      "Startups, small businesses, and marketing teams that need professional design without a full-time hire",
    short_description:
      "I'm a freelance graphic designer creating logos, brand identities, social media graphics, pitch decks, and marketing assets for startups and small businesses that want to stand out.",
    mission:
      "Give small businesses and startups sharp, consistent visual branding that builds trust and makes them look bigger and more established than they are.",
  },
  {
    id: "video",
    label: "Video editor",
    industry: "Video editing",
    offer:
      "Video editing for YouTube, podcasts, ads, and short-form clips — pacing, captions, and motion graphics",
    target_customer:
      "YouTubers, podcasters, coaches, and brands that publish video content regularly",
    short_description:
      "I'm a freelance video editor cutting YouTube videos, podcast clips, ads, and short-form content — pacing, captions, motion graphics, and hooks that keep viewers watching to the end.",
    mission:
      "Help creators and brands grow faster by turning raw footage into polished, binge-worthy videos so they can stay focused on creating instead of editing.",
  },
  {
    id: "copy",
    label: "Copywriter",
    industry: "Copywriting",
    offer: "Website copy, landing pages, email sequences, and ad copy",
    target_customer:
      "SaaS companies, e-commerce brands, and agencies that need copy that converts",
    short_description:
      "I'm a freelance copywriter writing landing pages, email sequences, website copy, and ads for SaaS and e-commerce brands — clear, persuasive words that turn visitors into customers.",
    mission:
      "Turn bland marketing copy into clear, persuasive messaging that helps businesses convert more of the traffic and audience they already have.",
  },
  {
    id: "smm",
    label: "Social media manager",
    industry: "Social media management",
    offer:
      "Social media management, content calendars, captions, and community engagement",
    target_customer:
      "Small businesses and personal brands that want consistent, growing social accounts",
    short_description:
      "I'm a freelance social media manager handling content calendars, posting, captions, and community engagement so businesses show up consistently on Instagram, TikTok, LinkedIn, and X.",
    mission:
      "Take social media off busy founders' plates and turn neglected accounts into consistent channels that actually bring in customers and community.",
  },
  {
    id: "web",
    label: "Web developer",
    industry: "Web development",
    offer:
      "Websites, landing pages, and web apps built with modern frameworks — design to deployment",
    target_customer:
      "Founders, small businesses, and agencies that need fast, reliable web development",
    short_description:
      "I'm a freelance web developer building fast, modern websites, landing pages, and web apps for founders and small businesses — from design to deployment, without agency overhead.",
    mission:
      "Give founders and small teams a reliable technical partner who ships fast, communicates clearly, and builds sites that actually load and convert.",
  },
  {
    id: "photo",
    label: "Photographer",
    industry: "Photography",
    offer: "Product, brand, and event photography",
    target_customer:
      "E-commerce brands, restaurants, and local businesses that need professional photos",
    short_description:
      "I'm a freelance photographer shooting product photos, brand imagery, and events for e-commerce stores and local businesses that need scroll-stopping visuals for their site and social.",
    mission:
      "Help brands sell more with photography that makes their products and spaces look as good in pixels as they do in person.",
  },
];

// Minimum characters before the user can move past the Confirm Profile step.
// The strategy generator needs enough context to produce useful keywords.
const MIN_MISSION_CHARS = 80;
const MIN_DESC_CHARS = 80;
// Shorter floor for "expand with AI" — needs enough signal to work from but
// can be looser than the move-forward gate.
const MIN_EXPAND_CHARS = 40;

export default function NewCampaignPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { push } = useToast();

  const [step, setStep] = useState(1);
  // "business" (default) finds buyers for a product; "freelancer" finds
  // client leads for a freelance business (UGC, design, video, copy, …).
  // The type is stored on the campaign and flips every AI prompt downstream
  // — strategy, search, scoring, and reply drafting all hunt hiring-side
  // posts instead of buyer-side posts.
  const [campaignType, setCampaignType] = useState("business");
  // What the user wants OUT of their leads: "sales" (buyers to convert),
  // "research" (market insight), or "data" (broad topic coverage). Stored on
  // the campaign and steers strategy generation + lead scoring server-side.
  const [campaignGoal, setCampaignGoal] = useState("sales");
  const [presetId, setPresetId] = useState("");
  const isFreelancer = campaignType === "freelancer";
  const [websiteUrl, setWebsiteUrl] = useState(() => searchParams.get("website") || "");
  const [analyzing, setAnalyzing] = useState(false);
  const [profile, setProfile] = useState({
    company_name: "",
    mission: "",
    short_description: "",
    target_customer: "",
    offer: "",
    product_or_service_classification: "",
    industry: "",
    location_served: [],
    competitors: [],
    tone_of_voice: "",
  });
  const [generating, setGenerating] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [expanding, setExpanding] = useState(""); // "mission" | "short_description" | ""

  // Brand voice — only collected here if the user hasn't set it up yet.
  const [hasExistingBrandVoice, setHasExistingBrandVoice] = useState(false);
  const [brandVoiceLoaded, setBrandVoiceLoaded] = useState(false);
  const [brandVoice, setBrandVoice] = useState({
    tone: "professional",
    useWords: "",
    avoidWords: "",
    bannedPhrases: "",
    complianceRules: "",
  });

  const [campaignName, setCampaignName] = useState("");
  const [dateRangeDays, setDateRangeDays] = useState(1);
  const [syncMode, setSyncMode] = useState("manual");
  const [autoSyncDaily, setAutoSyncDaily] = useState(false);
  // Collavue searches X (Twitter), LinkedIn, and Reddit for public posts.
  // At least one platform must stay selected.
  const [platforms, setPlatforms] = useState(["x", "linkedin", "reddit"]);
  const togglePlatform = (key) =>
    setPlatforms((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((p) => p !== key)
          : prev // keep at least one platform selected
        : [...prev, key]
    );
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getBrandVoice();
        if (cancelled) return;
        setHasExistingBrandVoice(!!v);
      } catch {
        // Treat read failure as "no voice set" so the user is offered the step.
      } finally {
        if (!cancelled) setBrandVoiceLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The brand-voice step only appears for users who haven't set one up yet.
  // Steps before strategy are always 1..3; the brand-voice step is 4 when
  // shown, otherwise sync settings collapse into step 4. The final review
  // ("sync") is the last step in either case.
  const showBrandVoiceStep = brandVoiceLoaded && !hasExistingBrandVoice;
  const SYNC_STEP = showBrandVoiceStep ? 5 : 4;
  const BRAND_VOICE_STEP = 4;
  const STEPS = showBrandVoiceStep
    ? [
        { id: 1, name: "Website" },
        { id: 2, name: "Confirm profile" },
        { id: 3, name: "Strategy" },
        { id: 4, name: "Brand voice" },
        { id: 5, name: "Sync" },
      ]
    : [
        { id: 1, name: "Website" },
        { id: 2, name: "Confirm profile" },
        { id: 3, name: "Strategy" },
        { id: 4, name: "Sync" },
      ];

  const splitCsv = (s) =>
    String(s || "")
      .split(/[,\n]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const defaultCampaignName = (name) =>
    `${name || "Campaign"} — ${isFreelancer ? "Client Leads" : "Lead Discovery"}`;

  const onAnalyze = async () => {
    setError("");
    if (!websiteUrl.trim())
      return setError(
        isFreelancer
          ? "Enter your portfolio or website URL, pick a template below, or click skip."
          : "Enter a website URL or click skip."
      );
    setAnalyzing(true);
    try {
      const r = await analyzeWebsite(websiteUrl, campaignType);
      setProfile((p) => ({ ...p, ...r.profile }));
      if (!campaignName && r.profile?.company_name)
        setCampaignName(defaultCampaignName(r.profile.company_name));
      setStep(2);
      push({ title: "Website analyzed", desc: r.siteName, variant: "success" });
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const onSkipAnalyze = () => {
    setStep(2);
  };

  // Pre-fill the profile from a freelance template. Keeps anything personal
  // the user already typed (their name, tone) and jumps straight to the
  // Confirm Profile step so they can tweak instead of write from scratch.
  const applyPreset = (p) => {
    setPresetId(p.id);
    setError("");
    setProfile((prev) => ({
      ...prev,
      industry: p.industry,
      offer: p.offer,
      target_customer: p.target_customer,
      mission: p.mission,
      short_description: p.short_description,
      product_or_service_classification: "Freelance services",
    }));
    setStep(2);
  };

  const missionLen = (profile.mission || "").trim().length;
  const descLen = (profile.short_description || "").trim().length;
  const profileReady =
    missionLen >= MIN_MISSION_CHARS && descLen >= MIN_DESC_CHARS;

  const onExpand = async (field) => {
    setError("");
    const text = field === "mission" ? profile.mission : profile.short_description;
    if (!text || text.trim().length < MIN_EXPAND_CHARS) {
      return setError(
        `Add at least ${MIN_EXPAND_CHARS} characters before asking the AI to expand.`
      );
    }
    setExpanding(field);
    try {
      const r = await expandProfileText(field, text, profile);
      setProfile((p) => ({ ...p, [field]: r.text }));
      push({ title: "Expanded", variant: "success" });
    } catch (e) {
      setError(e.message);
    } finally {
      setExpanding("");
    }
  };

  const onGenerate = async () => {
    setError("");
    if (!profileReady) {
      return setError(
        `Mission needs ${MIN_MISSION_CHARS}+ chars and description needs ${MIN_DESC_CHARS}+ chars before generating.`
      );
    }
    setGenerating(true);
    try {
      const r = await generateCampaignStrategy(profile, campaignType, campaignGoal);
      const s = r.strategy || {};
      // The new backend prompt also returns `competitors` (auto-discovered).
      // Merge them back into profile.competitors so the campaign carries the
      // expanded list forward and the user sees it during review.
      if (Array.isArray(s.competitors) && s.competitors.length) {
        setProfile((p) => {
          const merged = Array.from(
            new Set([...(p.competitors || []), ...s.competitors])
          );
          return { ...p, competitors: merged };
        });
      }
      setStrategy(s);
      if (!campaignName)
        setCampaignName(defaultCampaignName(profile.company_name));
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const onCreate = async () => {
    setError("");
    if (!campaignName.trim()) return setError("Give the campaign a name.");
    setCreating(true);
    try {
      // Persist brand voice first if the user filled it in during this flow.
      if (showBrandVoiceStep) {
        const filledAnything =
          brandVoice.tone !== "professional" ||
          brandVoice.useWords.trim() ||
          brandVoice.avoidWords.trim() ||
          brandVoice.bannedPhrases.trim() ||
          brandVoice.complianceRules.trim();
        if (filledAnything) {
          try {
            await saveBrandVoice({
              tone: brandVoice.tone,
              useWords: splitCsv(brandVoice.useWords),
              avoidWords: splitCsv(brandVoice.avoidWords),
              bannedPhrases: splitCsv(brandVoice.bannedPhrases),
              complianceRules: brandVoice.complianceRules,
            });
          } catch (e) {
            // Don't block campaign creation on brand-voice save failure.
            console.warn("saveBrandVoice failed:", e);
          }
        }
      }

      const out = await createCampaign({
        name: campaignName.trim(),
        campaignType,
        campaignGoal,
        websiteUrl,
        companyName: profile.company_name,
        mission: profile.mission,
        shortDescription: profile.short_description,
        targetCustomer: profile.target_customer,
        offer: profile.offer,
        industry: profile.industry,
        location: profile.location_served,
        competitors: profile.competitors,
        toneOfVoice: profile.tone_of_voice,
        primaryKeywords: strategy?.primary_keywords || [],
        buyingIntentPhrases: strategy?.buying_intent_phrases || [],
        problemKeywords: strategy?.problem_keywords || [],
        competitorKeywords: strategy?.competitor_keywords || [],
        negativeKeywords: strategy?.negative_keywords || [],
        // Reddit search seeds itself from these; the sync also discovers and
        // persists more as it goes.
        targetSubreddits: strategy?.target_subreddits || [],
        leadQualificationRules: strategy?.lead_qualification_rules || [],
        explicitLeadDescription: strategy?.explicit_lead_description || "",
        whatWeOffer: strategy?.what_we_offer || "",
        responseGuidelines: strategy?.response_guidelines || [],
        disallowedResponseBehavior: strategy?.disallowed_response_behavior || [],
        platforms,
        syncMode,
        autoSyncDaily,
        dateRangeDays,
      });
      push({ title: "Campaign created", variant: "success" });
      nav(`/campaigns/${out.id}?autoSync=1`);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <PageHeader
        title="New campaign"
        subtitle={`Set up your lead discovery in ${STEPS.length} steps.`}
      />

      <Stepper step={step} steps={STEPS} />

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 border border-rose-200 bg-rose-50 rounded-sm text-sm text-rose-700">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          {error}
        </div>
      )}

      <StrategyGenerationModal open={generating} freelancer={isFreelancer} />

      {step === 1 && (
        <Card>
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            What are you finding leads for?
          </h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <TypeCard
              active={!isFreelancer}
              title="A business or product"
              desc="Find people talking about the problem your product solves."
              onClick={() => {
                setCampaignType("business");
                setPresetId("");
              }}
            />
            <TypeCard
              active={isFreelancer}
              title="My freelance services"
              desc="UGC, design, video, copywriting… Find brands and founders looking to hire."
              onClick={() => setCampaignType("freelancer")}
            />
          </div>

          <h3 className="mt-6 text-sm font-semibold text-[#0B1038]">
            What do you want from your leads?
          </h3>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            This tunes what the AI searches for and how it scores each post —
            you can change it later on the campaign page.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {CAMPAIGN_GOALS.map((g) => (
              <TypeCard
                key={g.v}
                active={campaignGoal === g.v}
                title={g.label}
                desc={g.desc}
                onClick={() => setCampaignGoal(g.v)}
              />
            ))}
          </div>

          {isFreelancer && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-[#0B1038]">
                Start from a template
              </h3>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                Pick your service and we'll pre-fill your profile — edit
                anything on the next step.
              </p>
              <div className="flex flex-wrap gap-2">
                {FREELANCE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p)}
                    className={`px-3 py-2 rounded-sm border text-sm ${
                      presetId === p.id
                        ? "bg-[#0B1038] text-white border-[#0B1038]"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="mt-5 border-t border-gray-100 pt-4" />
            </div>
          )}

          <h3 className="mt-5 font-display text-lg text-[#0B1038] tracking-tight flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-600" />
            {isFreelancer
              ? "Or analyze your portfolio / website (optional)"
              : "Enter your website's URL"}
          </h3>
          <p className="text-sm text-gray-600 mt-1.5">
            {isFreelancer
              ? "Have a portfolio, personal site, or link-in-bio page? We'll read it to pre-fill your profile."
              : "We'll extract information to determine what posts to monitor."}
          </p>
          <div className="mt-4 flex gap-2">
            <input
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder={
                isFreelancer
                  ? "https://yourportfolio.com"
                  : "https://yourcompany.com"
              }
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
            <button
              onClick={onAnalyze}
              disabled={analyzing}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-60"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing
                </>
              ) : (
                <>Analyze</>
              )}
            </button>
          </div>
          <button
            onClick={onSkipAnalyze}
            className="mt-3 text-sm text-gray-600 hover:text-[#0B1038]"
          >
            Skip and enter manually →
          </button>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            {isFreelancer
              ? "Confirm your freelance profile"
              : "Confirm your business profile"}
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Edit anything that's wrong. Mission and Short description need at
            least <strong>{MIN_MISSION_CHARS} characters</strong> each — the
            better this is, the better the{" "}
            {isFreelancer ? "client leads" : "leads"}.
          </p>
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.05 } },
            }}
          >
            <motion.div
              variants={fieldFadeVariants}
              className="mt-5 grid sm:grid-cols-2 gap-3"
            >
              <Field
                label={isFreelancer ? "Your name or brand" : "Company name"}
                value={profile.company_name}
                onChange={(v) => setProfile({ ...profile, company_name: v })}
              />
              <Field
                label={
                  isFreelancer
                    ? "Niche (e.g. UGC, graphic design)"
                    : "Industry"
                }
                value={profile.industry}
                onChange={(v) => setProfile({ ...profile, industry: v })}
              />
              <Field
                label={isFreelancer ? "Services you offer" : "Offer / product"}
                value={profile.offer}
                onChange={(v) => setProfile({ ...profile, offer: v })}
              />
              <Field
                label={
                  isFreelancer
                    ? "Ideal client (e.g. DTC brands, coaches)"
                    : "Target customer (e.g. SaaS founders, indie devs)"
                }
                value={profile.target_customer}
                onChange={(v) =>
                  setProfile({ ...profile, target_customer: v })
                }
              />
              <Field
                label="Tone of voice"
                value={profile.tone_of_voice}
                onChange={(v) => setProfile({ ...profile, tone_of_voice: v })}
              />
              <Field
                label={
                  isFreelancer
                    ? "Classification (e.g. Freelance services)"
                    : "Classification (e.g. SaaS, B2B)"
                }
                value={profile.product_or_service_classification}
                onChange={(v) =>
                  setProfile({
                    ...profile,
                    product_or_service_classification: v,
                  })
                }
              />
            </motion.div>
            <motion.div variants={fieldFadeVariants}>
              <FieldWithCounter
                label="Short description"
                helper={
                  isFreelancer
                    ? "Describe the services you offer clients. 80+ characters required."
                    : "Describe your product. 80+ characters required."
                }
                value={profile.short_description}
                minChars={MIN_DESC_CHARS}
                onChange={(v) =>
                  setProfile({ ...profile, short_description: v })
                }
                onExpand={() => onExpand("short_description")}
                expanding={expanding === "short_description"}
              />
            </motion.div>
            <motion.div variants={fieldFadeVariants}>
              <FieldWithCounter
                label="Mission"
                helper={
                  isFreelancer
                    ? "What do you help clients achieve? 80+ characters required."
                    : "What is your brand solving? 80+ characters required."
                }
                value={profile.mission}
                minChars={MIN_MISSION_CHARS}
                onChange={(v) => setProfile({ ...profile, mission: v })}
                onExpand={() => onExpand("mission")}
                expanding={expanding === "mission"}
              />
            </motion.div>
            <motion.div variants={fieldFadeVariants}>
              <CsvChipField
                label="Regions served (optional)"
                helper="Comma-separated — e.g. US, EU, UK. Optional."
                value={profile.location_served || []}
                onChange={(v) =>
                  setProfile({ ...profile, location_served: v })
                }
                placeholder="US, EU, UK"
              />
            </motion.div>
            <motion.div variants={fieldFadeVariants}>
              <CsvChipField
                label={
                  isFreelancer
                    ? "Competing agencies / marketplaces (optional)"
                    : "Known competitors (optional)"
                }
                helper={
                  isFreelancer
                    ? "Where else might your clients look — e.g. Fiverr, Upwork, a content agency. Helps catch posts like \"looking for a Fiverr alternative\". Optional."
                    : "Comma-separated — we'll auto-discover more if you leave it blank. Optional."
                }
                value={profile.competitors || []}
                onChange={(v) => setProfile({ ...profile, competitors: v })}
                placeholder={
                  isFreelancer ? "Fiverr, Upwork" : "Acme, Widgetry, RivalCo"
                }
              />
            </motion.div>
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B1038]"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <motion.button
              whileTap={{ scale: profileReady ? 0.97 : 1 }}
              onClick={onGenerate}
              disabled={generating || !profileReady}
              title={
                !profileReady
                  ? "Fill in mission and short description (80+ chars each)"
                  : ""
              }
              className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              <span>Generate strategy (5 credits)</span>
            </motion.button>
          </div>
          {!profileReady && (
            <div className="mt-3 text-xs text-gray-500">
              {missionLen < MIN_MISSION_CHARS && (
                <div>
                  Mission needs {MIN_MISSION_CHARS - missionLen} more
                  character{MIN_MISSION_CHARS - missionLen === 1 ? "" : "s"}.
                </div>
              )}
              {descLen < MIN_DESC_CHARS && (
                <div>
                  Short description needs {MIN_DESC_CHARS - descLen} more
                  character{MIN_DESC_CHARS - descLen === 1 ? "" : "s"}.
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {step === 3 && strategy && (
        <Card>
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            Review your campaign strategy
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Edit the keyword sets if needed. These power lead discovery and
            scoring — the more accurate, the better the matches.
          </p>

          <ChipField
            label="Primary keywords"
            value={strategy.primary_keywords || []}
            onChange={(v) => setStrategy({ ...strategy, primary_keywords: v })}
          />
          <ChipField
            label={
              isFreelancer
                ? "Hiring intent phrases (what clients post)"
                : "Buying intent phrases"
            }
            value={strategy.buying_intent_phrases || []}
            onChange={(v) =>
              setStrategy({ ...strategy, buying_intent_phrases: v })
            }
          />
          <ChipField
            label={
              isFreelancer ? "Client pain keywords" : "Problem keywords"
            }
            value={strategy.problem_keywords || []}
            onChange={(v) => setStrategy({ ...strategy, problem_keywords: v })}
          />
          <ChipField
            label={
              isFreelancer
                ? "Agency / marketplace keywords"
                : "Competitor keywords"
            }
            value={strategy.competitor_keywords || []}
            onChange={(v) =>
              setStrategy({ ...strategy, competitor_keywords: v })
            }
          />
          <ChipField
            label="Negative keywords"
            value={strategy.negative_keywords || []}
            onChange={(v) => setStrategy({ ...strategy, negative_keywords: v })}
          />
          <Field
            label={
              isFreelancer
                ? "What an ideal client lead looks like"
                : "Explicit lead description"
            }
            value={strategy.explicit_lead_description || ""}
            onChange={(v) =>
              setStrategy({ ...strategy, explicit_lead_description: v })
            }
            textarea
          />
          <Field
            label="What we offer"
            value={strategy.what_we_offer || ""}
            onChange={(v) => setStrategy({ ...strategy, what_we_offer: v })}
            textarea
          />

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B1038]"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={() =>
                setStep(showBrandVoiceStep ? BRAND_VOICE_STEP : SYNC_STEP)
              }
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54]"
            >
              {showBrandVoiceStep ? (
                <>
                  Next: brand voice <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next: sync settings <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </Card>
      )}

      {showBrandVoiceStep && step === BRAND_VOICE_STEP && (
        <Card>
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            Set your brand voice
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Collavue uses this to draft replies that sound like you. Fill in
            what you can, or skip and tune it later from the Brand Voice page.
          </p>

          <Label>Tone</Label>
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button
                key={t}
                onClick={() => setBrandVoice({ ...brandVoice, tone: t })}
                className={`px-3 py-1.5 rounded-sm border text-sm capitalize ${
                  brandVoice.tone === t
                    ? "bg-[#0B1038] text-white border-[#0B1038]"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <Field
            label="Words to use"
            value={brandVoice.useWords}
            onChange={(v) => setBrandVoice({ ...brandVoice, useWords: v })}
          />
          <Field
            label="Words to avoid"
            value={brandVoice.avoidWords}
            onChange={(v) => setBrandVoice({ ...brandVoice, avoidWords: v })}
          />
          <Field
            label="Banned phrases"
            value={brandVoice.bannedPhrases}
            onChange={(v) =>
              setBrandVoice({ ...brandVoice, bannedPhrases: v })
            }
          />
          <Field
            label="Compliance rules"
            value={brandVoice.complianceRules}
            onChange={(v) =>
              setBrandVoice({ ...brandVoice, complianceRules: v })
            }
            textarea
          />

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={() => setStep(3)}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B1038]"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep(SYNC_STEP)}
                className="text-sm text-gray-600 hover:text-[#0B1038]"
              >
                Skip for now
              </button>
              <button
                onClick={() => setStep(SYNC_STEP)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54]"
              >
                Next: sync settings <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Card>
      )}

      {step === SYNC_STEP && (
        <Card>
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            Platform & sync settings
          </h2>
          <div className="mt-5 space-y-4">
            <Field
              label="Campaign name"
              value={campaignName}
              onChange={setCampaignName}
            />

            <div>
              <Label>Date range</Label>
              <div className="flex flex-wrap gap-2">
                {[0.125, 1, 7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setDateRangeDays(d)}
                    className={`px-3 py-1.5 rounded-sm border text-sm ${
                      dateRangeDays === d
                        ? "bg-[#0B1038] text-white border-[#0B1038]"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {d < 1 ? "Last 3 hours" : `Last ${d} ${d === 1 ? "day" : "days"}`}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Min 3 hours, max 90 days.
              </p>
            </div>

            <div>
              <Label>Search platforms</Label>
              <p className="text-xs text-gray-500 mb-2">
                {isFreelancer
                  ? "Pick where Collavue looks for public posts from brands and founders hiring — or venting about needing — what you do. Keep at least one selected."
                  : "Pick where Collavue looks for public posts that match your campaign. Keep at least one selected."}
              </p>
              <div className="flex flex-wrap gap-4 mt-1">
                <PlatformToggle
                  label="X (Twitter)"
                  checked={platforms.includes("x")}
                  onChange={() => togglePlatform("x")}
                />
                <PlatformToggle
                  label="LinkedIn"
                  checked={platforms.includes("linkedin")}
                  onChange={() => togglePlatform("linkedin")}
                />
                <PlatformToggle
                  label="Reddit"
                  checked={platforms.includes("reddit")}
                  onChange={() => togglePlatform("reddit")}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Every source you pick is searched in the same sync, for the same
                credit cost — and each one is capped separately, so adding Reddit
                never crowds out your X or LinkedIn leads.
              </p>
            </div>

            <div>
              <Label>Sync frequency</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSyncMode("daily");
                    setAutoSyncDaily(true);
                  }}
                  aria-pressed={autoSyncDaily}
                  className={`text-left p-4 rounded-sm border transition-colors ${
                    autoSyncDaily
                      ? "border-[#0B1038] bg-[#0B1038]/[0.03] ring-1 ring-[#0B1038]"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3.5 h-3.5 rounded-full border ${
                        autoSyncDaily
                          ? "border-[#0B1038] bg-[#0B1038]"
                          : "border-gray-400"
                      }`}
                    />
                    <span className="text-sm font-semibold text-[#0B1038]">
                      Daily auto-sync
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-600 text-white">
                      Recommended
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-600">
                    Wake up to fresh leads. Every day Collavue searches X,
                    LinkedIn, and Reddit for you and emails you a digest.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSyncMode("manual");
                    setAutoSyncDaily(false);
                  }}
                  aria-pressed={!autoSyncDaily}
                  className={`text-left p-4 rounded-sm border transition-colors ${
                    !autoSyncDaily
                      ? "border-[#0B1038] bg-[#0B1038]/[0.03] ring-1 ring-[#0B1038]"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3.5 h-3.5 rounded-full border ${
                        !autoSyncDaily
                          ? "border-[#0B1038] bg-[#0B1038]"
                          : "border-gray-400"
                      }`}
                    />
                    <span className="text-sm font-semibold text-[#0B1038]">
                      Manual only
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-600">
                    Run syncs yourself whenever you want. You can turn on the
                    daily email later.
                  </p>
                </button>
              </div>
              {autoSyncDaily && (
                <div className="mt-3 p-3 border border-blue-200 bg-blue-50 rounded-sm text-xs text-blue-900 leading-relaxed">
                  <strong className="block mb-1">
                    Your daily email digest includes:
                  </strong>
                  · New leads found overnight (with hot-lead count)
                  <br />· Results of your saved lead-assistant commands (e.g.
                  “find every lead that mentions pricing”)
                  <br />· One-click links to your inbox and an unsubscribe link
                  in every email
                </div>
              )}
            </div>

            <div className="p-3 border border-gray-200 bg-gray-50 rounded-sm text-sm text-gray-700">
              Cost:{" "}
              <strong className="text-[#0B1038]">
                {collavueSyncCost(dateRangeDays, platforms)} credits
              </strong>{" "}
              per sync (scales with your lookback window  15 credits at 3 hours,
              100 credits at 90 days). cr == credits.
              {platforms.includes("reddit") && (
                <span className="block mt-1 text-xs text-gray-500">
                  Includes a {collavueRedditSurcharge(dateRangeDays)}-credit
                  Reddit surcharge — Reddit is searched through a paid API that
                  bills us per request. Untick Reddit to drop it.
                </span>
              )}
              {autoSyncDaily && (
                <span className="block mt-1 text-xs text-gray-500">
                  Daily auto-sync will charge{" "}
                  {collavueSyncCost(dateRangeDays, platforms)} credits/day.
                  You'll get an email summary after each run with an
                  unsubscribe link.
                </span>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={() =>
                setStep(showBrandVoiceStep ? BRAND_VOICE_STEP : 3)
              }
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B1038]"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={onCreate}
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-60"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Create campaign
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}

function TypeCard({ active, title, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left p-4 rounded-sm border transition-colors ${
        active
          ? "border-[#0B1038] bg-[#0B1038]/[0.03] ring-1 ring-[#0B1038]"
          : "border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-3.5 h-3.5 rounded-full border ${
            active ? "border-[#0B1038] bg-[#0B1038]" : "border-gray-400"
          }`}
        />
        <span className="text-sm font-semibold text-[#0B1038]">{title}</span>
      </div>
      <p className="mt-1.5 text-xs text-gray-600">{desc}</p>
    </button>
  );
}

function PlatformToggle({ label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`inline-flex items-center gap-2 text-sm ${
        disabled ? "text-gray-400" : "text-gray-700"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="accent-blue-600"
      />
      {label}
    </label>
  );
}

const fieldFadeVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

// Modal-shaped strategy generator. Sits on top of everything, blocks
// interaction so the user can't navigate away or re-trigger the request.
function StrategyGenerationModal({ open, freelancer = false }) {
  const lines = freelancer
    ? [
        "Reading your services and niche…",
        "Mapping the clients who hire for this…",
        "Drafting hiring-intent phrases (\"looking for a UGC creator\"…)…",
        "Finding client pain points to watch on X, LinkedIn, and Reddit…",
        "Tuning negative keywords to filter other freelancers' self-promo…",
      ]
    : [
        "Reading your profile…",
        "Discovering competitors and adjacent products…",
        "Mapping pain points to X (Twitter), LinkedIn, and Reddit searches…",
        "Drafting buying-intent phrases…",
        "Tuning negative keywords to filter spam…",
      ];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    setI(0);
    const id = setInterval(() => {
      setI((n) => (n + 1) % lines.length);
    }, 1400);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="strategy-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#0B1038]/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Generating campaign strategy"
        >
          <motion.div
            initial={{ y: 16, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 8, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative w-full max-w-md bg-white rounded-md border border-gray-200 shadow-xl overflow-hidden"
          >
            <div className="relative p-6">
              <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-blue-100/80 to-transparent"
                animate={{ x: ["0%", "400%"] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <div className="relative flex items-center gap-3">
                <div className="relative w-11 h-11 rounded-md bg-blue-50 ring-1 ring-blue-200 flex items-center justify-center">
                  <Wand2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
                    AI strategy
                  </div>
                  <h3 className="font-display text-xl text-[#0B1038] tracking-tight">
                    Generating your strategy
                  </h3>
                </div>
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              </div>

              <div className="mt-4 text-sm text-gray-600 h-5 overflow-hidden">
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={i}
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -12, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="inline-block"
                  >
                    {lines[i]}
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="mt-4 h-1.5 bg-blue-50 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-blue-600 rounded-full"
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{ width: "60%" }}
                />
              </div>

              <p className="mt-4 text-xs text-gray-500">
                This usually takes 5–15 seconds. Please don't close this
                window or hit back — your strategy is being written.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Stepper({ step, steps }) {
  return (
    <ol className="flex flex-wrap items-center gap-y-2 text-sm">
      {steps.map((s, i) => (
        <li key={s.id} className="flex items-center">
          <span
            className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${
              step >= s.id
                ? "bg-[#0B1038] text-white"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {s.id}
          </span>
          <span
            className={`ml-2 mr-3 ${
              step >= s.id ? "text-[#0B1038] font-medium" : "text-gray-500"
            }`}
          >
            {s.name}
          </span>
          {i < steps.length - 1 && (
            <span
              className={`w-6 h-px ${
                step > s.id ? "bg-[#0B1038]" : "bg-gray-200"
              } mr-3`}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

function Card({ children }) {
  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-md p-4 sm:p-6">
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1.5 mt-3">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, textarea = false }) {
  return (
    <div className="mt-3">
      <Label>{label}</Label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />
      )}
    </div>
  );
}

// Textarea with min-character counter and an "Expand with AI" button. The
// expand action is only enabled once the textarea has enough text — short
// inputs don't give the model anything useful to work with.
function FieldWithCounter({
  label,
  helper,
  value,
  minChars,
  onChange,
  onExpand,
  expanding,
}) {
  const len = (value || "").trim().length;
  const ok = len >= minChars;
  const expandReady = len >= MIN_EXPAND_CHARS && !expanding;
  return (
    <div className="mt-3">
      <Label>{label}</Label>
      {helper && (
        <p className="text-xs text-gray-500 -mt-1 mb-1.5">{helper}</p>
      )}
      <div className="relative">
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 pr-3 border rounded-sm text-sm outline-none focus:ring-1 transition-colors ${
            ok
              ? "border-gray-300 focus:border-blue-600 focus:ring-blue-600"
              : "border-amber-300 focus:border-amber-500 focus:ring-amber-500"
          }`}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onExpand}
          disabled={!expandReady}
          title={
            !expandReady && !expanding
              ? `Add at least ${MIN_EXPAND_CHARS} characters first`
              : ""
          }
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-[#0B1038] disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {expanding ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Sparkles className="w-3 h-3" />
          )}
          {expanding ? "Expanding…" : "Expand with AI"}
        </button>
        <span
          className={`text-xs tabular-nums ${
            ok ? "text-emerald-600" : "text-amber-600"
          }`}
        >
          {len} / {minChars}
          {ok ? " ✓" : ` chars (${minChars - len} more needed)`}
        </span>
      </div>
    </div>
  );
}

// Chip field that also accepts comma-separated input — type the whole list
// at once and hit Enter or comma. Existing chips still removable via the X.
function CsvChipField({ label, helper, value, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const commit = (raw) => {
    const parts = String(raw || "")
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const merged = Array.from(new Set([...(value || []), ...parts]));
    onChange(merged);
    setInput("");
  };
  return (
    <div className="mt-3">
      <Label>{label}</Label>
      {helper && (
        <p className="text-xs text-gray-500 -mt-1 mb-1.5">{helper}</p>
      )}
      <div className="flex flex-wrap gap-2 mb-2">
        {(value || []).map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-sm border border-blue-100"
          >
            {c}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="hover:text-rose-700"
              aria-label={`Remove ${c}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => {
            const v = e.target.value;
            // If the user typed a comma, eagerly commit everything before it
            // so the chip list stays in sync as they type.
            if (v.includes(",")) {
              commit(v);
            } else {
              setInput(v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(input);
            }
          }}
          onBlur={() => commit(input)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />
        <button
          type="button"
          onClick={() => commit(input)}
          className="px-3 py-2 rounded-sm border border-gray-300 text-sm hover:bg-gray-50 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

function ChipField({ label, value, onChange, placeholder = "Add…" }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setInput("");
  };
  return (
    <div className="mt-3">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {(value || []).map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-sm border border-blue-100"
          >
            {c}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="hover:text-rose-700"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-sm border border-gray-300 text-sm hover:bg-gray-50 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}
